<script setup lang="ts">
import type {
  StripeError,
  StripeErrorType,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent,
  StripePaymentElementChangeEvent,
} from '@stripe/stripe-js'
import { IconExclamationCircle } from '@tabler/icons-vue'
import { computed, onBeforeUnmount, ref, toRef, watch } from 'vue'

import BaseButton, { type BaseButtonProps } from '@tix/ds/components/atoms/BaseButton.vue'
import StatusMessage from '@tix/ds/components/atoms/StatusMessage.vue'
import type { TrackContext } from '@tix/error-tracker'
import { isValidationError as isApiValidationError } from '@tix/openapi-sdk'
import { useStripeElements } from '@tix/stripe'

import { getEnv } from '@/helpers/environment'
import type { BillingDetailsResult } from '@/helpers/identityForm/billingDetails'
import { buildStripeConfirmReturnUrl } from '@/helpers/paymentReturn'
import { sourceTypeForExpressPayment } from '@/helpers/payments/expressCheckout'

import { errorTracker } from '@/services/errorTracking'

import type {
  CheckoutPreflightResult,
  GuestCheckoutContext,
  PaidCheckoutCartResult,
} from '@/components/donations/checkout.types'

import ValidationError, { isValidationError } from '@/errors/ValidationError'

export type StripePaymentProps = {
  accountId: string
  currencyCode: string
  pmcId: string
  /** Stripe minor units (may be below the chargeable minimum — composable reports `invalid_amount`). */
  amount: number | null
  checkoutCart: (body: {
    gateway_data: { source: string; source_type: string }
    guest_identity_id?: string
    captcha?: { provider: 'hcaptcha'; token: string }
  }) => Promise<PaidCheckoutCartResult>
  resolveGuestContext: () => Promise<GuestCheckoutContext>
  checkoutPreflight: () => Promise<CheckoutPreflightResult>
  /** Called at Pay time so identity validation is not eager on mount. */
  getBillingDetails: () => BillingDetailsResult
  paymentButtonLabel?: string
  payButtonIcon?: BaseButtonProps['iconBefore']
  /** When true, Pay is disabled (e.g. donation cart modify still in flight). */
  pending?: boolean
}

const props = defineProps<StripePaymentProps>()

const emit = defineEmits<{
  (e: 'complete', data: { email: string; order: string; amount: number }): void
  /** Parent remounts this component (`:key`) to recover from a terminal composable error. */
  (e: 'reload'): void
  /** Payment Element focused, or Express Checkout wallet button clicked. */
  (e: 'focus'): void
  /** True while a card/wallet checkout run is in flight — parent freezes the amount widget. */
  (e: 'update:submitting', value: boolean): void
}>()

const { PaymentElement, ExpressCheckoutElement, submit, createPaymentMethod, confirmPayment, fatalError, status } =
  useStripeElements({
    publishableKey: getEnv().config.stripe.publishable_key,
    accountId: props.accountId,
    currencyCode: props.currencyCode,
    amount: toRef(props.amount),
    pmcId: toRef(props.pmcId),
  })

const sourceType = ref<string | null>(null)
const submitting = ref(false)
const submitError = ref<Error | StripeError | null>(null)
const hasWalletButtons = ref(false)

watch(submitting, (value) => emit('update:submitting', value), { immediate: true })
onBeforeUnmount(() => emit('update:submitting', false))

// Shared readiness for both submit paths: Stripe loaded, no submit already in flight, and no
// cart-modify request in flight. `payButtonDisabled` (cards) additionally requires a selected
// payment method; the express element (wallets) never gets one until confirm, so it can't use that check.
const readyToSubmit = computed(() => status.value === 'ready' && !submitting.value && !props.pending)
const payButtonDisabled = computed(() => !readyToSubmit.value || sourceType.value == null)
const buttonLabel = computed(() => (submitting.value ? 'Processing…' : (props.paymentButtonLabel ?? 'Pay')))

const expressCheckoutOptions = {
  buttonType: { applePay: 'donate' as const, googlePay: 'donate' as const },
}

type CheckoutRun = {
  sourceType: string
  /** Card path requires a selected method; wallets never report one, so they skip this check. */
  requireSelectedPaymentMethod: boolean
}

async function runCheckout({ sourceType, requireSelectedPaymentMethod }: CheckoutRun): Promise<boolean> {
  // Defensive: `payButtonDisabled`/`readyToSubmit` already gate the UI, but a wallet sheet opened
  // before `pending` flipped true could still fire `confirm`; block re-entry here too.
  if (submitting.value || props.pending) {
    const err = new Error('Payment session not ready. Try again.')
    submitError.value = err
    trackError(err, { sourceType, requireSelectedPaymentMethod })
    return false
  }

  submitError.value = null
  submitting.value = true

  try {
    const billingDetailsResult = props.getBillingDetails()
    if (billingDetailsResult.status === 'error') {
      submitError.value = new ValidationError(billingDetailsResult.error)
      return false
    }

    const preflight = await props.checkoutPreflight()
    if (preflight.status === 'error') {
      // UI only — soft preflight copy (validation / no cart) and mutation failures already tracked upstream as `donation.cart.*`.
      submitError.value = preflight.error
      return false
    }

    // 1. Validate the element + collect wallet data.
    const submitResult = await submit()

    if (submitResult.error || submitResult.stripeError) {
      const err = submitResult.error ?? submitResult.stripeError ?? new Error('Payment submission failed.')

      submitError.value = err
      trackError(err, { operation: 'payment.submit' })
      return false
    }

    if (requireSelectedPaymentMethod && !submitResult.selectedPaymentMethod) {
      // Tracking for debugging — submitResult.error guard above should catch this.
      const err = new Error('Please select a payment method and try again.')

      submitError.value = err
      trackError(err, { operation: 'payment.requireSelectedPaymentMethod' })
      return false
    }

    // 2. Manual PaymentMethod creation — billing_details is the integrator's job (fields are 'never').
    const paymentMethodResult = await createPaymentMethod({
      params: { billing_details: billingDetailsResult.data },
    })

    if (!paymentMethodResult.paymentMethod) {
      const err = paymentMethodResult.error ?? paymentMethodResult.stripeError

      submitError.value = err
      trackError(err, { operation: 'payment.createPaymentMethod' })
      return false
    }

    const { paymentMethod } = paymentMethodResult

    // Guest upsert + captcha run only after a PaymentMethod exists so abandoned / invalid Element submits do not create identities or burn tokens.
    const guestContext = await props.resolveGuestContext()

    if (guestContext.status === 'error') {
      // Captcha dismiss/expire is a client ValidationError — UI only, shopper can retry Pay.
      if (isValidationError(guestContext.error)) {
        submitError.value = new ValidationError('Please try again.')
        return false
      }
      trackError(guestContext.error, { operation: 'payment.resolveGuestContext', guestContext })
      submitError.value = guestContext.error
      return false
    }

    const { receiptEmail, guest_identity_id, captcha } = guestContext

    // 3. First POST — tix-api answers 402 intent_requires_action with a client_secret.
    const intentResult = await props.checkoutCart({
      gateway_data: { source: paymentMethod.id, source_type: sourceType },
      guest_identity_id,
      captcha,
    })

    if (intentResult.status === 'error') {
      if (!intentResult.tracked) trackError(intentResult.error, { operation: 'payment.checkoutCart.1' })
      submitError.value = intentResult.error
      return false
    }

    if (intentResult.status !== 'requires_action') {
      const err = new Error('Expected intent_requires_action from checkout')

      submitError.value = err
      trackError(err, { operation: 'payment.checkoutCart.1', intentResult })
      return false
    }

    const { client_secret: clientSecret } = intentResult

    // 4. Confirm. May navigate away (redirect-only PMs) — resume on return_url; otherwise resolves here (cards, in-iframe 3DS).
    // tix-api already attached this PaymentMethod to the intent, so confirm against it rather than
    // letting Stripe re-collect from the Payment Element — the wallet paths never populate it.
    const confirmResult = await confirmPayment({
      clientSecret,
      confirmParams: {
        payment_method: paymentMethod.id,
        return_url: buildStripeConfirmReturnUrl({
          returnUrl: guestContext.returnUrl,
          sourceType,
          purchaserId: guest_identity_id,
        }),
      },
    })

    if (!confirmResult.paymentIntent) {
      const err = confirmResult.error ?? confirmResult.stripeError

      submitError.value = err
      trackError(err, { operation: 'payment.confirmPayment' })
      return false
    }

    // 5. Second POST — settle against the confirmed intent.
    const result = await props.checkoutCart({
      gateway_data: { source: confirmResult.paymentIntent.id, source_type: sourceType },
      guest_identity_id,
      captcha,
    })

    if (result.status === 'requires_action') {
      const err = new Error('Unexpected intent_requires_action on settle')

      submitError.value = err
      trackError(err, { operation: 'payment.checkoutCart.2', result })
      return false
    }

    if (result.status === 'error') {
      if (!result.tracked) trackError(result.error, { operation: 'payment.checkoutCart.2' })
      submitError.value = result.error
      return false
    }

    emit('complete', {
      // Wallets collect their own email (emailRequired); prefer it over the guest fallback.
      email: receiptEmail,
      order: result.data.ticket_order?._data?.[0]?.order_number ?? '—',
      amount: props.amount!,
    })
    return true
  } catch (e) {
    const err = e as Error
    trackError(err, { operation: 'payment.runCheckout' })
    submitError.value = err
    return false
  } finally {
    submitting.value = false
  }
}

function handleSubmit() {
  return runCheckout({
    sourceType: sourceType.value ?? 'card',
    requireSelectedPaymentMethod: true,
  })
}

async function handleExpressConfirm(event: StripeExpressCheckoutElementConfirmEvent) {
  const succeeded = await runCheckout({
    sourceType: sourceTypeForExpressPayment(event.expressPaymentType),
    requireSelectedPaymentMethod: false,
  })

  // Surface the real failure reason in the wallet sheet; `reason` is a fixed Stripe
  // enum so the detail goes in `message`.
  if (!succeeded) event.paymentFailed({ reason: 'fail', message: submitError.value?.message })
}

function trackError<C>(error: Error | StripeError, ctx?: TrackContext<C>) {
  if (isValidationError(error) || isApiValidationError(error)) return

  const isStripeError = !(error instanceof Error)

  const errorContext = {
    operation: 'payment.checkout',
    status: status.value,
    sourceType: sourceType.value,
    submitting: submitting.value,
    pending: props.pending,
    ...ctx,
  }

  if (!isStripeError) return errorTracker.track(error, errorContext)

  const dontTrackThese: StripeErrorType[] = ['validation_error', 'card_error']
  if (dontTrackThese.includes(error.type)) return

  errorTracker.track(error.message ?? `Stripe error: ${error.type}`, {
    stripeError: error,
    ...errorContext,
  })
}

function onPaymentElementChange(event: StripePaymentElementChangeEvent) {
  sourceType.value = event.value?.type ?? null
}

function onExpressCheckoutReady(event: StripeExpressCheckoutElementReadyEvent) {
  const methods = event.availablePaymentMethods
  hasWalletButtons.value = methods != null && Object.values(methods).some((isAvailable) => isAvailable)
}

watch(fatalError, (e) => e && trackError(e as Error | StripeError))
</script>

<template>
  <div class="stripe-payment-container">
    <StatusMessage
      v-if="status === 'invalid_amount'"
      :dismissible="false"
      :icon="IconExclamationCircle"
      message="This amount is below the minimum payment amount."
      variant="warning"
    />

    <template v-else>
      <template v-if="!fatalError">
        <div :aria-disabled="!readyToSubmit" class="express-checkout" :class="{ 'is-disabled': !readyToSubmit }">
          <!-- ExpressCheckout buttons don't fire `focus` events, so we map `click` to `focus` (so it's consumed up the three for GA event tracking) -->
          <ExpressCheckoutElement
            :options="expressCheckoutOptions"
            @click="emit('focus')"
            @confirm="handleExpressConfirm"
            @ready="onExpressCheckoutReady"
          />
        </div>

        <p v-if="hasWalletButtons" class="wallet-separator">Or pay by card</p>
      </template>

      <PaymentElement
        v-if="!fatalError"
        :readOnly="!readyToSubmit"
        @change="onPaymentElementChange"
        @focus="emit('focus')"
      />

      <StatusMessage
        v-else
        buttonLabel="Reload"
        :dismissible="false"
        :icon="IconExclamationCircle"
        :message="`Payment unavailable: ${fatalError.message}`"
        variant="error"
        @button-click="emit('reload')"
      />

      <StatusMessage v-if="submitError" :icon="IconExclamationCircle" :message="submitError.message" variant="error" />

      <BaseButton
        v-if="!fatalError"
        :disabled="payButtonDisabled"
        :iconBefore="payButtonIcon"
        type="button"
        @click="handleSubmit"
      >
        {{ buttonLabel }}
      </BaseButton>
    </template>
  </div>
</template>

<style scoped>
.wallet-separator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  color: var(--text-primary);

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-block-start: 1px solid var(--neutral-border-color);
  }
}

.stripe-payment-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.express-checkout {
  &.is-disabled {
    pointer-events: none;
    opacity: 0.5;
  }
}
</style>
